import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import Send from "../../core/communication"
import { useDispatch, useSelector } from "react-redux"
import NewAssistanceScreen from "../../screen/NewAssistanceScreen"
import { useProjects, useScope } from "../SelectProjectCompanyContainer"
import {
  findNode,
  formatProjectTreeSelect,
  getProjectIdsFromTree,
} from "../../core/utils/projectsTreeSelect"
import { sort } from "fast-sort"
import {
  useTechnicianUsers,
} from "../../core/utils/hooks/useUsers"
import { setData as setDataAction } from "../../core/redux/app/Actions"
import { customToast } from "../../components/Toast"
import { exportPlanActions } from "../../core/utils/exports"
import { filterActionArray } from "../ActionPlanContainer"
import { isObjectEffectivelyEmpty } from "../../components/NewChatComponents"
import { useStartPageQueries } from "../../core/utils/hooks/useStartPageQueries"
import { ChatProvider } from "../../core/context/chatContext"
import useStartPageCachedData from "../../core/utils/hooks/useStartPageCachedData"

const periodOptions = [
  {
    value: "all",
    label: "Todo o período",
  },
  {
    value: "today",
    label: "Hoje",
  },
  {
    value: "lastWeek",
    label: "Últimos 7 dias",
  },
  {
    value: "lastMonth",
    label: "Últimos 30 dias",
  },
]

const AssistanceInitialContainer = () => {
  const { scope, scopeType } = useScope()
  const { changeScope, projects } = useProjects()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state?.app?.user)
  const currentUserRole = useSelector((state) => state.app.currentUserRole)
  const organizations = useSelector((state) => state?.app.organizations)
  const startPageFilters = useSelector((state) => state.app?.startPageFilters)

  const [currentTab, setCurrentTab] = useState(1)

  const [data, setData] = useState([])
  const [tagPossibility, setTagPossibility] = useState([])

  const [filter, setFilter] = useState()
  const [values, setValues] = useState({})
  const [projectsPeriodFilter, setProjectsPeriodFilter] = useState(null)
  const [showFilters, setShowFilters] = useState(false)

  const [valueTree, setValueTree] = useState(undefined)
  const [reloadActionPlan, setReloadActionPlan] = useState(false)
  const [projectIds, setProjectIds] = useState(null)
  const treeData = formatProjectTreeSelect(organizations)

  const isAdminOrManagerUser = ["USER_ADMIN", "USER_MANAGER"].includes(currentUserRole)
  const isTecUser = currentUserRole === "USER_TEC"

  const [technicianSelect, setTechnicianSelect] = useState([])
  const technicalList = isAdminOrManagerUser
    ? useTechnicianUsers(projectIds, valueTree == undefined || valueTree == null)
    : []

  const [loadingActionPlan, setLoadingActionPlan] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState(null)

  const { exportPlan, loadingExport } = exportPlanActions(
    filterActionArray(data, filter),
    "plan_actions_",
  )

  const projectIdCheck =
    (!projectIds || projectIds === null || projectIds !== "") && startPageFilters?.project !== null
      ? startPageFilters?.project
      : startPageFilters?.project === null && projectIds
        ? projectIds
        : null

  const handleChangeTab = ({ value }) => {
    setCurrentTab(value)
  }

  const handleProjectsPeriodFilter = (value) => {
    if (value) {
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["period"]: value } }))
      setProjectsPeriodFilter(value)
    } else {
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["period"]: null } }))
      setProjectsPeriodFilter(null)
    }
  }

  const handleChangeTecnhicianSelect = (value) => {
    if (value) {
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["technician"]: value } }))
      setTechnicianSelect(value)
    } else {
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["technician"]: null } }))
      setTechnicianSelect(null)
    }
  }

  useEffect(() => {
    if (!treeData.length) return

    if (!valueTree) {
      setProjectIds("")
      return
    }

    let selectedIds = []

    selectedIds = Array.isArray(valueTree)
      ? valueTree.flatMap((id) => getProjectIdsFromTree(treeData, id))
      : getProjectIdsFromTree(treeData, valueTree)

    setProjectIds(selectedIds.map((value) => `'${value}'`).join(", "))
  }, [treeData, JSON.stringify(valueTree)])

  useEffect(() => {
    loadPlanActions()
  }, [scope?.id])

  useEffect(() => {
    if (technicianSelect?.length > 0) {
      loadPlanActions()
    }
  }, [technicianSelect])

  useEffect(() => {
    if (startPageFilters && !isObjectEffectivelyEmpty(startPageFilters)) {
      setShowFilters(true)
    } else {
      setShowFilters(false)
    }
  }, [])

  const prevProject = useRef(startPageFilters?.project);

  useEffect(() => {
    const currentProject = startPageFilters?.project;
    const previousProject = prevProject.current;

    const projectWasRemoved = previousProject && currentProject === null;
    const projectWasChanged = previousProject && currentProject?.id !== previousProject?.id;

    if (projectWasRemoved || projectWasChanged) {
      if (startPageFilters?.technician) {
        dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["technician"]: null } }))
      }
    }

    prevProject.current = currentProject;
  }, [startPageFilters?.project]);

  const {
    loadingAppointments,
    loadingAssistance,
    refetchingAppointments,
    isInitialLoading,
    refetchAll
  } = useStartPageQueries({
    user,
    scope,
    scopeType,
    startPageFilters,
    projectIds: projectIdCheck,
    currentUserRole,
    projects,
    fetchOnMount: true
  })

  const { finishedData, openedData, appointmentsData } = useStartPageCachedData(scope, startPageFilters)

  const onChange = (field, value) => {
    dispatch(setDataAction({ startPageFilters: { ...startPageFilters, [field]: value } }))
    setValues({ ...values, [field]: value })
  }

  const getAllChildrenValues = (node) => {
    if (!node.children) return [node.value]
    return node.children.flatMap(getAllChildrenValues)
  }

  const handleChangeTree = (selectedValue) => {
    if (!selectedValue) {
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["project"]: null } }))
      setValueTree(null)
      setProjectIds("")
      loadPlanActions()
    } else {
      const selectedNode = findNode(treeData, selectedValue)

      if (!selectedNode) return

      setValueTree(selectedValue)
      dispatch(setDataAction({ startPageFilters: { ...startPageFilters, ["project"]: selectedValue } }))
      const values = Array.isArray(selectedValue) ? selectedValue : [selectedValue]
      setProjectIds(
        values
          .map((value) => value)
          .map((value) => `'${value}'`)
          .join(", "),
      )
      loadPlanActions(selectedValue)
    }
  }

  const applyFilterData = (data) => {
    if (!values || !values["search"]) return data

    const searchValue = values["search"].toLowerCase()

    return data.filter((row) =>
      row.userNames.every((user) => user?.name.toLowerCase().includes(searchValue)),
    )
  }

  const goToScheduleDetails = (schedule) => {
    changeScope(schedule?.projectId, "project")
    dispatch(setDataAction({ shouldRedirectToUsers: true }))
    navigate(
      `/farmer/profile/${schedule?.userNames[0]?.id}?context=${schedule?.id}&projectId=${schedule?.projectId}&category=schedule&scheduleDetails`,
      { state: { fromAssistanceList: schedule } },
    )
  }

  const goToAssistanceDetails = (assistance) => {
    console.log("DFEFSDFSD", assistance)
    //setPageName("Atendimento")
    changeScope(assistance?.projectId, "project")
    dispatch(setDataAction({ shouldRedirectToUsers: true }))
    navigate(
      `/farmer/profile/${assistance?.userIds[0]}?context=${assistance?.id}&projectId=${assistance?.projectId}&category=${currentTab === 1 ? "open" : "closed"}&assistanceDetails`,
      { state: { fromAssistanceList: assistance } },
    )
  }

  const goToGetLink = () => {
    navigate(`/mobileform`)
  }

  const saveReload = () => {
    setReloadActionPlan(true)
  }

  const loadPlanActions = async (value = null) => {
    try {
      setLoadingActionPlan(true)
      const query = `/scale-action/all-action-profile/projects`
      const extraQuery = currentUserRole !== "USER_ADMIN" ? `projects='${value ?? scope.id}'` : ""

      const response = await Send.get(`${query}`, `${extraQuery}`)

      if (response && response?.length > 0) {
        const list = Array.isArray(response) ? response.flat() : response

        const formattedList = (() => {
          if (technicianSelect?.length > 0 && isAdminOrManagerUser) {
            return list.filter((item) =>
              technicianSelect.some(
                (tec) =>
                  item.internal_responsible === tec?.email ||
                  item.external_responsible === tec?.email,
              ),
            )
          }

          if (isTecUser) {
            return list.filter(
              (item) =>
                item.internal_responsible === user?.email ||
                item.external_responsible === user?.email,
            )
          }

          return list
        })()

        const formatted = sort(formattedList).by([
          { asc: (u) => typeof u.isComplete === "string" },
          { asc: (u) => u.finish_at },
        ])

        setData(
          formatted.map((item) =>
            item?.tags ? { ...item, tags: JSON.parse(item.tags) } : { ...item, tags: [] },
          ),
        )

        const tags = formatted.filter((item) => item?.tags).map((item) => JSON.parse(item?.tags))
        const output = tags.flatMap((item) => JSON.parse(item).map((str) => str.trim()))
        const unique = _.uniq(output)
        setTagPossibility(unique)
      } else {
        setData([])
      }
    } catch (error) {
      console.log({ error })
    } finally {
      setLoadingActionPlan(false)
    }
  }

  useEffect(() => {
    if (reloadActionPlan) {
      loadPlanActions()
      setReloadActionPlan(false)
    }

    ; () => setReloadActionPlan(false)
  }, [reloadActionPlan, loadPlanActions])

  const handleCompleteAction = async (actionId, isComplete) => {
    try {
      const response = await Send.put(`/scale-action-producer/${actionId}`, {
        isComplete: isComplete ? null : new Date(),
      })

      if (response?.id) {
        setData((old) =>
          old.map((row) =>
            row.actionId === response.id ? { ...row, isComplete: response.isComplete } : row,
          ),
        )
        customToast("Status da ação alterado.", "success")
      }
    } catch (error) {
      console.log("Error", error)
      customToast("Erro ao alterar status da ação", "error")
    }
  }

  const changeTag = async (value, isNew, item) => {
    const oldTags = typeof item.tags === "string" ? JSON.parse(item.tags) : item.tags
    if (item?.actionId) {
      try {
        const response = await Send.put(`/scale-action-producer/${item?.actionId}`, {
          tags: JSON.stringify(
            oldTags.includes(value)
              ? oldTags.filter((row) => row !== value)
              : oldTags.concat(value),
          ),
        })

        if (response?.id && response?.tags) {
          setData((old) =>
            old.map((row) =>
              row.actionId === response.id ? { ...row, tags: response.tags } : row,
            ),
          )
          if (isNew) {
            setTagPossibility((old) => old.concat(value))
          }
          customToast(
            item.tags.includes(value) ? "Tag removida da ação" : "Tag adicionada a ação.",
            "success",
          )
        }
      } catch (error) {
        console.log("Error", error)
        customToast("Erro ao adicionar tag", "error")
      }
    }
  }

  const changeFilter = (field, value) => {
    setFilter((old) => ({ ...old, [field]: value }))
  }

  const handleShowFilters = () => {
    setShowFilters((prev) => !prev)
  }

  const emails = data
    .filter((item) => item?.internal_responsible?.includes("@") || item?.external_responsible?.includes("@"))
    .map(({ external_responsible, internal_responsible }) => ({ label: external_responsible || internal_responsible, value: external_responsible || internal_responsible }))
  const uniqueEmails = _.uniqBy(emails, "value")

  const handleSelectUser = (id) => {
    setSelectedUserId(id)
  }

  const resetStartPageFilters = () => {
    dispatch(setDataAction({ startPageFilters: null }))

    setTimeout(() => {
      refetchAll()
    }, 0)
  }

  return (
    <NewAssistanceScreen
      handleChangeTecnhicianSelect={handleChangeTecnhicianSelect}
      technicianOptions={technicalList}
      handleChangeTab={handleChangeTab}
      appointmentsData={appointmentsData}
      listData={currentTab === 1 ? openedData : finishedData}
      numberOfOpen={openedData?.length || 0}
      numberOfClose={finishedData?.length || 0}
      goToScheduleDetails={goToScheduleDetails}
      goToAssistanceDetails={goToAssistanceDetails}
      goToGetLink={goToGetLink}
      isAdminOrManagerUser={isAdminOrManagerUser}
      isTecUser={isTecUser && user?.projectUser?.length > 1}
      onChange={onChange}
      periodOptions={periodOptions}
      projectId={scope?.id}
      treeData={treeData}
      valueTree={valueTree}
      handleChangeTree={handleChangeTree}
      handleCompleteAction={handleCompleteAction}
      changeTag={changeTag}
      tagPossibility={tagPossibility}
      changeFilter={changeFilter}
      filter={filter}
      uniqueEmails={uniqueEmails}
      handleProjectsPeriodFilter={handleProjectsPeriodFilter}
      projectsPeriodFilter={projectsPeriodFilter}
      data={filterActionArray(data, filter)}
      handleSelectUser={handleSelectUser}
      selectedUserId={selectedUserId}
      handleShowFilters={handleShowFilters}
      showFilters={showFilters}
      loadingAppointments={loadingAppointments}
      isRefetchingAppointmentsData={refetchingAppointments || isInitialLoading}
      loadingOpenedOrFinished={loadingAssistance}
      loadingActionPlan={loadingActionPlan}
      exportPlan={exportPlan}
      saveReload={saveReload}
      startPageFilters={startPageFilters}
      handleFetchFilters={refetchAll}
      resetStartPageFilters={resetStartPageFilters}
    />
  )
}

const NewAssistanceContainer = () => {
  return (
    <ChatProvider>
      <AssistanceInitialContainer />
    </ChatProvider>
  )
}

export default NewAssistanceContainer
